/**
 * Shared chips / badges (design §7.3).
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Role, Technique } from '@/lib/types';

const TECHNIQUE_STYLE: Record<Technique, string> = {
  Open: 'bg-pizarra-tinte text-pizarra',
  Laparoscopic: 'bg-acento-tinte text-acento-tinta',
  Robotic: 'bg-ambar-tinte text-ambar-tinta',
  Endoscopic: 'bg-salvia-tinte text-salvia',
  Other: 'bg-superficie-2 text-tinta-2 border border-linea',
};

/** Display-label key per stored enum value (the stored value never changes).
 * `as const` keeps the values as literal unions so `t(\`common.techniques.${key}\`)`
 * is accepted by the typed i18next keys. */
export const TECHNIQUE_LABEL_KEY = {
  Open: 'open',
  Laparoscopic: 'laparoscopic',
  Robotic: 'robotic',
  Endoscopic: 'endoscopic',
  Other: 'other',
} as const satisfies Record<Technique, string>;

export const ROLE_LABEL_KEY = {
  'Lead surgeon': 'leadSurgeon',
  'First assistant': 'firstAssistant',
  'Second assistant': 'secondAssistant',
  Supervisor: 'supervisor',
} as const satisfies Record<Role, string>;

export function TechniqueChip({ technique, className }: { technique: Technique; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-md px-2.5 text-[12px] font-medium leading-none print:border print:border-linea-fuerte print:bg-transparent print:text-tinta-2',
        TECHNIQUE_STYLE[technique],
        className,
      )}
    >
      {t(`common.techniques.${TECHNIQUE_LABEL_KEY[technique]}`)}
    </span>
  );
}

export function RoleChip({ role, className }: { role: Role; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center rounded-md border border-linea bg-superficie-2 px-2.5 text-[12px] font-medium leading-none text-tinta-2 print:border-linea-fuerte print:bg-transparent print:text-tinta-2',
        className,
      )}
    >
      {t(`common.roles.${ROLE_LABEL_KEY[role]}`)}
    </span>
  );
}

/**
 * 8px status dot, no label: terracotta when the procedure had a complication,
 * sage when it did not. Used by every compact row (dashboard, library) so the
 * same fact never renders at two different sizes.
 */
export function StatusDot({
  occurred,
  grade,
  className,
}: {
  occurred: boolean;
  grade?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const title = occurred
    ? t('common.complications.withGrade', { grade: grade ?? '' })
    : t('common.complications.none');
  return (
    <span
      title={title}
      role="img"
      aria-label={title}
      className={cn(
        'h-2 w-2 shrink-0 rounded-full',
        occurred ? 'bg-terracota' : 'bg-salvia',
        className,
      )}
    />
  );
}

/** 8px dot + 12px text: terracotta "Complication" / sage "No complications" */
export function ComplicationStatus({ occurred, className }: { occurred: boolean; className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-[12px]', className)}>
      <span
        className={cn('h-2 w-2 rounded-full print:border print:border-tinta-3', occurred ? 'bg-terracota' : 'bg-salvia')}
        aria-hidden
      />
      <span className={occurred ? 'text-terracota' : 'text-salvia'}>
        {occurred ? t('common.complications.occurred') : t('common.complications.none')}
      </span>
    </span>
  );
}

/** Folio badge: mono-label on surface-2 with a line border */
export function FolioBadge({ folio, className }: { folio: string; className?: string }) {
  return (
    <span
      className={cn(
        'estilo-mono-etiqueta inline-flex items-center rounded-md border border-linea bg-superficie-2 px-1.5 py-0.5 text-tinta-2 print:border-linea-fuerte print:bg-transparent print:text-tinta-2',
        className,
      )}
    >
      {folio}
    </span>
  );
}
